import { IdAndNameOf } from './IApiBaseResource';
import { IApiClient } from './IApiClient';
import { IApiNode } from './IApiNode';
import { ApiServerType } from './IApiServerType';
import { IApiStream } from './IApiStream';
import { IApiServerStream } from './IApiServerStream';

export enum ApiAutoSync {
  On = 'ON',
  Off = 'OFF',
  Na = 'NA',
}

export enum ApiConnectionStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  NA = 'NA',
  DISABLED = 'DISABLED',
  UNKNOWN = 'UNKNOWN',
}

export interface IApiServerBase {
  id: string;
  name: string;
  type: ApiServerType;
  tradingClient?: {
    id: string;
    enabled: boolean;
  };
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
}

export interface IApiTradingConnections {
  online: string[];
  offline: string[];
  disabled: string[];
}

export interface IApiServer extends IApiServerBase {
  clients: IApiClient[];
  node: IdAndNameOf<IApiNode>;
  clientSymbolsCount: number;
  autosync: ApiAutoSync;
  autoSyncDisabled?: boolean;
  connectionStatus: ApiConnectionStatus;
  tradingConnections?: IApiTradingConnections;
  pricingConnectionStatus?: ApiConnectionStatus;
  /**
   * Server reporting client connection status (ONLINE | OFFLINE | DISABLED); only with the riskManagement feature (LB-5266)
   */
  reportingConnections?: ApiConnectionStatus;
}

/**
 * cTrader Manager connection used for risk reporting; write shape of POST /nodes/{id}/servers and PUT /servers/{id},
 * allowed only for type=cTrader (LB-4732). plant/environment: 1-32 chars
 */
export interface IApiCTraderManagerSettings {
  plant: string;
  environment: string;
  managerLogin: string;
  managerPassword: string;
}

/**
 * Read shape of GET /servers/{id}: BE derives host/port from plant/environment; the password is returned in clear
 */
export interface IApiCTraderManagerSettingsDetails extends IApiCTraderManagerSettings {
  host: string;
  port: number;
}

export interface IApiServerCreate {
  name: string;
  type: ApiServerType;
  throttlingInterval?: number;
  /**
   * Independent markout (risk reporting) on this server; default false (LB-4701)
   */
  riskReportingEnabled?: boolean;
  /**
   * cTrader only (LB-4732); BE rejects it for other types
   */
  cTraderManagerSettings?: IApiCTraderManagerSettings;
}

export interface IApiServerDetails {
  name: string;
  throttlingInterval?: number;
  node: IdAndNameOf<IApiNode>;
  pricingClient?: IApiPricingClient;
  settings?: IApiServerSettings;
  defaultSettings?: {
    pricingSession: IApiMTPricingClientSettingsBase;
    tradingSession: IApiMTTradingClientSettingsBase;
  };
  tradingClients?: IApiTradingClient[];
  streams?: IApiServerStream[];
  type: ApiServerType;
  /**
   * Independent markout (risk reporting) on this server; default false (LB-4701)
   */
  riskReportingEnabled?: boolean;
  /**
   * cTrader only, present once set (LB-4732); kept by BE while risk reporting is off
   */
  cTraderManagerSettings?: IApiCTraderManagerSettingsDetails;
}

export interface IApiClientCreate {
  serverId: IApiServer['id'];
  streamId: IApiStream['id'];
  enabled?: boolean;
  settings?: IApiMTTradingClientSettingsBase;
}

export interface IApiPricingClient {
  clientId: string;
  clientSymbolsCount: number;
  enabled: boolean;
  status: ApiConnectionStatus;
  /**
   * Not specified for ctrader
   */
  settings?: IApiMTPricingClientSettingsBase;
}

export interface IApiMTPricingClientSettingsBase {
  /**
   * can be empty if "Symbols" is specified
   */
  securities?: string;
  /**
   * can be empty if "Securities" is specified
   */
  symbols?: string;
  useSymbolsConjunction: boolean;
}

/**
 * `settings` of GET /servers/{id} — FIX shape (cTrader): per-client FIX hosts
 */
export interface IApiServerFixSettings {
  pricingClientUrl: string;
  tradingClientUrl: string;
}

/**
 * `settings` of GET /servers/{id} — MT4/MT5 shape
 */
export interface IApiServerMtSettings {
  configServerUrl: string;
  preset: IApiServerPreset;
}

export type IApiServerSettings = IApiServerFixSettings | IApiServerMtSettings;

export interface IApiServerPreset {
  serverUrl: string;
  tradingClientIds: string[];
  pricingClientId: string;
  /**
   * Optional, because it may be unspecified
   */
  streams?: IApiMtServerStreamSettings[];
  execOptions: IApiExecOptions;
  riskReporting?: {
    reportingClientIds: string[];
  };
}

export interface IApiMtServerStreamSettings {
  name: string;
  tradingGroups: string;
  tradingEnabled: boolean;
  pricingEnabled: boolean;
  useSymbolsConjunction: boolean;
  rejectClientRequestIfOffline: boolean;
  /**
   * can be empty if "Symbols" is specified
   */
  securities?: string;
  /**
   * can be empty if "Securities" is specified
   */
  symbols?: string;
}

export interface IApiExecOptions {
  /**
   * on MT4 < 180
   */
  clientRequestTimeout: number;
  /**
   * on MT4 < 30
   */
  dealerRequestTimeout: number;
  orderExecutionTimeout: number;
  orderCommentOnActivation: string;
  orderCommentOnCancel: string;
  orderCommentOnStopLoss: string;
  orderCommentOnStopOut: string;
  orderCommentOnTakeProfit: string;
  sendUserExtraData: boolean;
}

export interface IApiTradingClient {
  clientId: string;
  clientSymbolsCount: number;
  enabled: boolean;
  status: ApiConnectionStatus;
}

export interface IApiMTTradingClientSettingsBase {
  groups: string;
  rejectClientRequestIfOffline: boolean;
  /**
   * can be empty if "Symbols" is specified
   */
  securities?: string;
  /**
   * can be empty if "Securities" is specified
   */
  symbols?: string;
  useSymbolsConjunction: boolean;
}
